/**
 * Cache pequeño para recursos GET compartidos.
 *
 * - Reutiliza la misma Promise mientras un request idéntico está en curso.
 * - Puede conservar brevemente la respuesta para absorber remounts inmediatos
 *   (por ejemplo, el ciclo de verificación de React.StrictMode en desarrollo).
 * - `force: true` ignora la respuesta reciente, pero sigue reutilizando un
 *   request idéntico que ya esté en curso.
 */
export const createRequestResourceCache = ({ ttlMs = 0 } = {}) => {
  const cachedValues = new Map();
  const inFlightRequests = new Map();

  const getValidCachedValue = (key) => {
    if (ttlMs <= 0) return undefined;

    const cached = cachedValues.get(key);
    if (!cached) return undefined;

    if (cached.expiresAt <= Date.now()) {
      cachedValues.delete(key);
      return undefined;
    }

    return cached.value;
  };

  const read = async (key, requestFactory, { force = false } = {}) => {
    const normalizedKey = String(key ?? "");

    if (!force) {
      const cachedValue = getValidCachedValue(normalizedKey);
      if (cachedValue !== undefined) {
        return cachedValue;
      }
    }

    if (inFlightRequests.has(normalizedKey)) {
      return inFlightRequests.get(normalizedKey);
    }

    const request = Promise.resolve().then(requestFactory);
    inFlightRequests.set(normalizedKey, request);

    try {
      const value = await request;

      if (ttlMs > 0) {
        cachedValues.set(normalizedKey, {
          value,
          expiresAt: Date.now() + ttlMs,
        });
      }

      return value;
    } catch (error) {
      cachedValues.delete(normalizedKey);
      throw error;
    } finally {
      if (inFlightRequests.get(normalizedKey) === request) {
        inFlightRequests.delete(normalizedKey);
      }
    }
  };

  const invalidate = (key) => {
    cachedValues.delete(String(key ?? ""));
  };

  const invalidateMatching = (predicate) => {
    for (const key of cachedValues.keys()) {
      if (predicate(key)) {
        cachedValues.delete(key);
      }
    }
  };

  const clear = () => {
    cachedValues.clear();
  };

  return {
    read,
    invalidate,
    invalidateMatching,
    clear,
  };
};
